import { InfoIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Heading,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import { gradientStops, linearGradient } from "@intentsify/brand";
import { useHasAccessTo } from "@intentsify/authorization/dist/react";
import { Button, ErrorBoundary } from "components";
import { PropsWithChildren } from "react";
import { useAtomValue } from "jotai";
import { screenAtom, screenOptionsAtom } from "utils";
import { Notifications } from "./components";
import { ProfileMenu } from "./components/ProfileMenu/ProfileMenu";
import { UserAgencyCompanySelector } from "./components/UserAgencyCompanySelector/UserAgencyCompanySelector";
import { useConfirm } from "components/ConfirmDialog/useConfirm";
import { useClearCdlCache } from "mutations/admin/useClearCdlCache";
import { MdCleaningServices } from "react-icons/md";
import { byLook, isBrandLook } from "theme/look";
import { TopBarColorMode } from "./TopBarColorMode";

const TopBar = (props: PropsWithChildren) => {
  const { name, info } = useAtomValue(screenAtom);
  const { subtitle } = useAtomValue(screenOptionsAtom);

  const hasAccessToNotifications = useHasAccessTo(
    "miscellaneous.notifications"
  );

  const hasAccessToClearCache = useHasAccessTo("admin.clearCache");
  const confirm = useConfirm();
  const clearCache = useClearCdlCache();
  const brandHeaderBg = useColorModeValue("white", "brand.400");
  const brandTitleColor = useColorModeValue("gray.900", "gray.50");
  const brandSubtitleColor = useColorModeValue("gray.500", "gray.400");

  const handleClearCache = async () => {
    const confirmed = await confirm({
      title: "Clear cache",
      description:
        "This clears all cached campaign & report data for every user. Continue?",
      confirmText: "Clear cache",
    });

    if (!confirmed) {
      return;
    }

    clearCache.mutate();
  };

  return (
    <>
      <Flex
        id="top-bar"
        bg={byLook(brandHeaderBg, "brand.400")}
        p={4}
        flexDir="column"
      >
        <Flex alignItems="center" justifyContent="space-between">
          {props.children ? (
            props.children
          ) : (
            <Heading
              color={byLook(brandTitleColor, "gray.300")}
              as="h2"
              size="sm"
              lineHeight="inherit"
            >
              {name}{" "}
              {info ? (
                <Tooltip label={info}>
                  <InfoIcon w={4} h={4} ml={2} />
                </Tooltip>
              ) : null}
            </Heading>
          )}

          <Flex>
            {hasAccessToClearCache && (
              <Flex mr={3} alignItems="center">
                <TopBarColorMode>
                  <Button
                    size="sm"
                    variant="outline-teal"
                    leftIcon={<MdCleaningServices size="16px" />}
                    isLoading={clearCache.isLoading}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onClick={handleClearCache}
                  >
                    Clear Cache
                  </Button>
                </TopBarColorMode>
              </Flex>
            )}

            <UserAgencyCompanySelector />

            {hasAccessToNotifications && (
              <Flex ml={5}>
                <ErrorBoundary>
                  <Notifications />
                </ErrorBoundary>
              </Flex>
            )}

            <Flex ml={2}>
              <ProfileMenu />
            </Flex>
          </Flex>
        </Flex>

        {subtitle && (
          <Heading
            color={byLook(brandSubtitleColor, "gray.300")}
            fontWeight={byLook("normal", undefined)}
            opacity={byLook(undefined, 0.8)}
            as="h3"
            size="xs"
            lineHeight="inherit"
          >
            {subtitle}
          </Heading>
        )}
      </Flex>
      {isBrandLook && (
        <Box
          h="3px"
          flexShrink={0}
          bgImage={linearGradient(gradientStops.bar)}
        />
      )}
    </>
  );
};

export { TopBar };
